import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { INPUT_HEIGHTS, BORDER_OPACITY } from "@/components/ui/constants";
import { cn } from "@/lib/utils";
import { useFormContext } from "react-hook-form";

type TextInputProps = {
	name: string;
	label?: string;
	labelAction?: React.ReactNode;
	placeholder?: string;
	value?: string;
	disabled?: boolean;
	readOnly?: boolean;
	onChange?: (value: string) => void;
	shouldUnregister?: boolean;
	intent?: "column" | "inlined";
	className?: string;
	autoComplete?: string;
	type?: string;
};

export default function TextInput({
	name,
	label,
	labelAction,
	placeholder,
	value,
	disabled,
	readOnly,
	onChange,
	shouldUnregister,
	intent,
	className,
	autoComplete,
	type,
}: TextInputProps) {
	const { control } = useFormContext();

	// Only pass defaultValue if value is explicitly provided
	// Otherwise, let FormField use the form's defaultValues
	const formFieldProps: any = {
		control,
		name,
		shouldUnregister,
	};
	if (value !== undefined) {
		formFieldProps.defaultValue = value;
	}

	return (
		<FormField
			{...formFieldProps}
			render={({ field }) => (
				<FormItem intent={intent}>
					{label ? (
						<div className="mb-1 flex items-center justify-between gap-2">
							<FormLabel intent={intent} className="mb-0">
								{label}
							</FormLabel>
							{labelAction}
						</div>
					) : null}

					<FormControl>
						<Input
							className={cn(
								!className && BORDER_OPACITY.standard,
								"flex-shrink-0",
								INPUT_HEIGHTS.form, // Use form height token (h-11 = 44px) instead of inline styles
								className
							)}
							style={{ boxSizing: 'border-box' }}
							disabled={disabled}
							readOnly={readOnly}
							placeholder={placeholder}
							autoComplete={autoComplete}
							type={type}
							{...field}
							// Ensure value is always a string to prevent uncontrolled->controlled warning
							value={field.value ?? ""}
							onChange={(e) => {
								field.onChange(e);
								onChange?.call(undefined, e.target.value);
							}}
						/>
					</FormControl>

					<FormMessage intent={intent} />
				</FormItem>
			)}
		/>
	);
}
